import { useState, useEffect } from "react";
import {
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { ComponentMetadata } from "./types/component";
import CustomTextField from "../../../components/common/Textfield";
import { useComponentContext } from "../../../contexts/ComponentContext";

const emptyForm: ComponentMetadata = {
  componentName: "",
  description: "",
  leadName: "",
  backupDeveloper: "",
  version: "",
};

const UpdateFooter = () => {
  const { componentsData, updateComponent } = useComponentContext();
  const [selectedComponent, setSelectedComponent] = useState("");
  const [newComponentName, setNewComponentName] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [formData, setFormData] = useState<ComponentMetadata>(emptyForm);

  useEffect(() => {
    if (selectedComponent) {
      // If the component exists in context, use it; otherwise, start with emptyForm
      setFormData(componentsData[selectedComponent] || emptyForm);
    }
  }, [selectedComponent, componentsData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    let key = selectedComponent;

    if (selectedComponent === "new") {
      if (!newComponentName) return; // require a name
      key = newComponentName.replace(/\s+/g, "_").toLowerCase(); // generate a key
    }

    updateComponent(key, {
      ...formData,
      componentName:
        selectedComponent === "new" ? newComponentName : formData.componentName,
    });

    setOpenModal(true);

    setSelectedComponent("");
    setNewComponentName("");
    setFormData(emptyForm);
  };

  return (
    <Paper sx={{ p: 3, mt: 2 }}>
      <Typography variant="h5" gutterBottom>
        Assign Component Lead
      </Typography>

      <Box component="form" onSubmit={handleSubmit}>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Select Component</InputLabel>
          <Select
            value={selectedComponent}
            label="Select Component"
            onChange={(e) => setSelectedComponent(e.target.value)}
          >
            {Object.keys(componentsData).map((key) => (
              <MenuItem key={key} value={key}>
                {componentsData[key].componentName}
              </MenuItem>
            ))}
            <MenuItem value="new">+ Create New Component</MenuItem>
            {selectedComponent === "new" && (
              <CustomTextField
                fullWidth
                label="New Component Name"
                value={newComponentName}
                onChange={(e) => setNewComponentName(e.target.value)}
              />
            )}
          </Select>
        </FormControl>
        <CustomTextField
          fullWidth
          margin="normal"
          label="Description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          disabled={!selectedComponent}
          multiline
          rows={3}
        />
        <CustomTextField
          fullWidth
          margin="normal"
          label="Lead Developer"
          name="leadName"
          value={formData.leadName}
          onChange={handleChange}
          disabled={!selectedComponent}
        />

        <CustomTextField
          fullWidth
          margin="normal"
          label="Backup Developer"
          name="backupDeveloper"
          value={formData.backupDeveloper}
          onChange={handleChange}
          disabled={!selectedComponent}
        />

        <CustomTextField
          fullWidth
          margin="normal"
          label="Version"
          name="version"
          value={formData.version}
          onChange={handleChange}
          disabled={!selectedComponent}
        />

        <Button
          type="submit"
          variant="contained"
          sx={{ mt: 2 }}
          disabled={!selectedComponent}
        >
          Save
        </Button>
      </Box>
      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="sm" // you can use "md" or "lg" if you want bigger
        fullWidth
      >
        <DialogTitle>Success</DialogTitle>
        <DialogContent>
          <Typography sx={{ py: 2 }}>
            Component assigned successfully!
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModal(false)} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default UpdateFooter;
